import { CollinsUserContextType } from "@/context/CollinsUserContext";
import { INavigationMenuItem } from "../../types/NavigationMenu";
import { UsePermissionsReturn } from "../../types/permissions.types";
import { dmnIdentity } from "../dmnIdentity";
import { GetEnvironmentPropsResponse } from "../environment";

const isProductionEnvironment = (
  enviromentsProps: GetEnvironmentPropsResponse | undefined
): boolean => {
  return (
    enviromentsProps?.ENVIRONMENT === "Production" ||
    enviromentsProps?.ENVIRONMENT === "Demo"
  );
};

/**
 * Helper type for permission context used by role-based permission helpers
 */
interface RolesPermissionContext {
  roles: UsePermissionsReturn["roles"];
  capabilities: UsePermissionsReturn["capabilities"];
  hasVenues: boolean;
  isWorkspaceInstance: boolean;
  enviromentsProps: GetEnvironmentPropsResponse | undefined;
}

/**
 * Helper type for permission context used by capability-based permission helpers
 */
interface CapabilitiesPermissionContext {
  capabilities: UsePermissionsReturn["capabilities"];
  hasVenues: boolean;
  isWorkspaceInstance: boolean;
  userIsDmn: boolean;
  enviromentsProps: GetEnvironmentPropsResponse | undefined;
}

/**
 * Calculates permissions using roles-based logic
 */
const getPermissionsFromRoles = (
  ctx: RolesPermissionContext,
  isFlagEnabled: (flagKey: string) => boolean
): Omit<NavShowPermissions, "showHelpTestAvailability"> => {
  const { roles, hasVenues, isWorkspaceInstance, enviromentsProps } = ctx;
  const isInternal = roles.derived.userIsInternal;

  return {
    canManageInternal: isInternal,
    showEnquiries:
      isFlagEnabled("showEnquiries") && (isInternal || roles.booking.update),
    showDiary: !isInternal && hasVenues && roles.booking.read,
    showFloorplan: !isInternal && hasVenues,
    showRunSheet: !isInternal && hasVenues && roles.booking.read,
    showReports:
      !isInternal &&
      (roles.booking.read || roles.payment.read || roles.bankTransfer.read),
    showCustomers: !isInternal && roles.customer.read,
    showSettings:
      isInternal ||
      roles.site.update ||
      roles.site.areas.update ||
      roles.site.availability.update ||
      roles.organisation.update ||
      roles.organisation.read ||
      roles.user.permissions.update,
    showsettingOrganisations:
      roles.organisation.read ||
      roles.organisation.update ||
      roles.auditLog.download,
    showSettingsSites:
      isInternal ||
      roles.site.update ||
      roles.site.areas.update ||
      roles.site.availability.update,
    showSettingsCopySiteRules:
      isInternal ||
      roles.site.update ||
      roles.site.areas.update ||
      roles.site.availability.update,
    showSettingsUsers: isInternal || roles.user.permissions.update,
    showSettingsUsersAndRoles: isInternal || roles.user.permissions.update,
    showAvailability: isInternal,
    showHelp: !isInternal && !isWorkspaceInstance,
    showHelpTestWaitlist: isInternal,
    showTesting: isInternal && !isProductionEnvironment(enviromentsProps),
    showAlphaFeatures: isInternal,
    showMore: isWorkspaceInstance,
    showMoreTestBookingRules: !isInternal,
    showMoreTestWaitlist: isInternal,
    showUserAccount: !isWorkspaceInstance
  };
};

/**
 * Calculates permissions using capabilities-based logic
 */
const getPermissionsFromCapabilities = (
  ctx: CapabilitiesPermissionContext,
  isFlagEnabled: (flagKey: string) => boolean
): Omit<NavShowPermissions, "showHelpTestAvailability"> => {
  const {
    capabilities,
    hasVenues,
    isWorkspaceInstance,
    userIsDmn,
    enviromentsProps
  } = ctx;
  const isInternal = capabilities.derived.userIsInternal;

  const hasBookingManagement =
    capabilities.manageVenueGroupBookings.thisVenueGroup ||
    capabilities.manageVenueBookings.some;

  const hasBookingConfirmation =
    capabilities.confirmVenueGroupBookings.thisVenueGroup ||
    capabilities.confirmVenueBookings.some;

  return {
    canManageInternal: isInternal,
    showEnquiries:
      isFlagEnabled("showEnquiries") &&
      (isInternal || hasBookingManagement || hasBookingConfirmation),
    showDiary: !isInternal && hasVenues,
    showFloorplan: !isInternal && hasVenues,
    showRunSheet: !isInternal && hasVenues,
    showReports: !isInternal && hasBookingManagement,
    showCustomers:
      !isInternal &&
      (capabilities.viewVenueGroupCustomers?.thisVenueGroup ||
        capabilities.viewVenueCustomers?.some),
    showSettings:
      isInternal ||
      capabilities.manageVenueBookingRules?.some ||
      capabilities.manageVenueGroupBookingRules?.thisVenueGroup ||
      capabilities.manageVenueGroupPermissions?.thisVenueGroup ||
      capabilities.manageVenuePermissions?.some,
    showsettingOrganisations:
      capabilities.manageVenueGroupBookingRules.thisVenueGroup,
    showSettingsSites:
      isInternal ||
      capabilities.manageVenueBookingRules?.some ||
      capabilities.manageVenueGroupBookingRules?.thisVenueGroup,
    showSettingsCopySiteRules:
      isInternal ||
      capabilities.manageVenueBookingRules?.some ||
      capabilities.manageVenueGroupBookingRules?.thisVenueGroup,
    showSettingsUsers:
      isInternal ||
      capabilities.manageVenueGroupPermissions?.thisVenueGroup ||
      capabilities.manageVenuePermissions?.some,
    showSettingsUsersAndRoles: isInternal,
    showAvailability: isInternal && capabilities.manageBookings,
    showHelp: !isInternal && !isWorkspaceInstance,
    showHelpTestWaitlist: userIsDmn,
    showTesting: isInternal && !isProductionEnvironment(enviromentsProps),
    showAlphaFeatures: isInternal || capabilities.hasEarlyAccessPreview,
    showMore: isWorkspaceInstance,
    showMoreTestBookingRules: !isInternal,
    showMoreTestWaitlist: userIsDmn,
    showUserAccount: !isWorkspaceInstance
  };
};

export const getFilterNavigationItems = (
  permissions: UsePermissionsReturn,
  enviromentsProps: GetEnvironmentPropsResponse | undefined,
  isFlagEnabled: (flagKey: string) => boolean,
  userContext?: CollinsUserContextType
): INavigationMenuItem[] => {
  const {
    showEnquiries,
    showDiary,
    showFloorplan,
    showRunSheet,
    showReports,
    showCustomers,
    showSettings,
    showsettingOrganisations,
    showSettingsSites,
    showSettingsCopySiteRules,
    showSettingsUsers,
    showSettingsUsersAndRoles,
    showAvailability,
    showHelp,
    showHelpTestAvailability,
    showHelpTestWaitlist,
    showTesting,
    showAlphaFeatures,
    showMore,
    showMoreTestBookingRules,
    showMoreTestWaitlist,
    showUserAccount,
    canManageInternal
  } = getNavShowPermissions(permissions, enviromentsProps, isFlagEnabled);

  const APP_BASE_URL = enviromentsProps?.APP_BASE_URL ?? "";
  const BASE_DMN_URL = enviromentsProps?.BASE_DMN_URL ?? "";

  const items: INavigationMenuItem[] = [
    {
      text: "Home",
      icon: "Dashboard",
      link: `${APP_BASE_URL}/dashboard`
    }
  ];

  if (showEnquiries) {
    items.push({
      text: "Enquiries",
      icon: "Contacts",
      link: "/enquiries"
    });
  }

  if (showDiary) {
    items.push({
      text: "Diary",
      icon: "Calendar",
      venue_picker: {
        path: "/diary"
      }
    });
  }

  if (showFloorplan) {
    items.push({
      text: "Floorplan",
      icon: "Map2",
      venue_picker: { path: "/floorplan" }
    });
  }

  if (showRunSheet) {
    const runsheetItem: INavigationMenuItem = {
      text: "Run sheet",
      icon: "ListTodo"
    };

    runsheetItem.venue_picker = { path: "/run-sheet" };
    items.push(runsheetItem);
  }

  if (showReports) {
    items.push({
      text: "Reports",
      icon: "LineChartSolid2",
      venue_picker: {
        path: `${APP_BASE_URL}/reports/{{venueId}}/overview`
      },
      link: `${APP_BASE_URL}/reports/{{venueId}}/overview`
    });
  }

  if (showCustomers) {
    items.push({
      text: "Customers",
      link: `${APP_BASE_URL}/customers`,
      icon: "ID"
    });
  }

  const settingsChildren: INavigationMenuItem[] = [];
  if (showsettingOrganisations) {
    const current = canManageInternal ? "" : "/current";
    settingsChildren.push({
      text: "Organisations",
      link: `${APP_BASE_URL}/organisations${current}`
    });
  }

  if (showSettingsSites) {
    settingsChildren.push({
      text: "Sites",
      link: `${APP_BASE_URL}/site-rules`
    });
  }

  if (showSettingsCopySiteRules) {
    settingsChildren.push({
      text: "Copy site rules",
      link: `${APP_BASE_URL}/copy-site-rules`
    });
  }

  if (showSettingsUsers) {
    settingsChildren.push({
      text: "Users",
      link: `${APP_BASE_URL}/manage-users`
    });
  }

  if (showSettingsUsersAndRoles) {
    settingsChildren.push({
      text: "Users & Roles",
      link: `${APP_BASE_URL}/users-and-roles/manage-users`
    });
  }

  if (showSettings) {
    items.push({
      text: "Settings",
      icon: "Settings2",
      children: settingsChildren
    });
  }

  if (showAvailability) {
    items.push({
      text: "Availability",
      link: `${APP_BASE_URL}/availability`,
      icon: "BookTick",
      children: []
    });
  }

  const showHelpChildren: INavigationMenuItem[] = [];
  showHelpChildren.push(
    {
      text: "Knowledge & Support",
      link: "https://access-support.force.com/Support/s/login/"
    },
    {
      text: "Test booking rules",
      link: `${APP_BASE_URL}/test-booking-rules`
    }
  );

  if (showHelpTestAvailability) {
    showHelpChildren.push({
      text: "Test availability rules",
      link: `${APP_BASE_URL}/test-availability-rules`
    });
  }

  if (showHelpTestWaitlist) {
    showHelpChildren.push({
      text: "Test waitlist times",
      link: `${APP_BASE_URL}/test-waitlist`
    });
  }

  if (showHelp) {
    items.push({
      text: "Help",
      icon: "CircleQuestionMark",
      children: showHelpChildren
    });
  }

  if (showTesting) {
    items.push({
      text: "Testing",
      link: `${APP_BASE_URL}/testing-panel`,
      icon: "Flask"
    });
  }

  if (showAlphaFeatures) {
    items.push({
      text: "Alpha Features",
      icon: "Flask",
      bottom: true,
      link: permissions.capabilities.hasEarlyAccessPreview
        ? "/settings"
        : `${APP_BASE_URL}/alpha-features`
    });
  }

  const moreChildren: INavigationMenuItem[] = [];
  if (showMoreTestBookingRules) {
    moreChildren.push({
      text: "Test booking rules",
      link: `${APP_BASE_URL}/test-booking-rules`
    });
  }

  if (showMoreTestWaitlist) {
    moreChildren.push({
      text: "Test waitlist times",
      link: `${APP_BASE_URL}/test-waitlist`
    });
  }

  moreChildren.push({
    text: "Test availability rules",
    link: `${APP_BASE_URL}/test-availability-rules`
  });

  if (showMore) {
    items.push({
      text: "More",
      icon: "Drag2",
      bottom: true,
      children: moreChildren
    });
  }

  if (showUserAccount) {
    items.push({
      text: `${userContext?.user?.first_name} ${userContext?.user?.last_name}`.trim(),
      icon: "CircleUser",
      children: [
        {
          text: "My account",
          link: `${BASE_DMN_URL}/my-account`
        },
        {
          text: "Knowledge & Support",
          link: "https://access-support.force.com/Support/s/login/"
        },
        {
          text: "Sign out",
          function: dmnIdentity.signOut
        }
      ],
      bottom: true
    });
  }

  return items;
};

type NavShowPermissions = {
  showEnquiries: boolean;
  showDiary: boolean;
  showFloorplan: boolean;
  showRunSheet: boolean;
  showReports: boolean;
  showCustomers: boolean;
  showSettings: boolean;
  showsettingOrganisations: boolean;
  showSettingsSites: boolean;
  showSettingsCopySiteRules: boolean;
  showSettingsUsers: boolean;
  showSettingsUsersAndRoles: boolean;
  showAvailability: boolean;
  showHelp: boolean;
  showHelpTestAvailability: boolean;
  showHelpTestWaitlist: boolean;
  showTesting: boolean;
  showAlphaFeatures: boolean;
  showMore: boolean;
  showMoreTestBookingRules: boolean;
  showMoreTestWaitlist: boolean;
  showUserAccount: boolean;
  canManageInternal: boolean;
};

const getNavShowPermissions = (
  permissions: UsePermissionsReturn,
  enviromentsProps: GetEnvironmentPropsResponse | undefined,
  isFlagEnabled: (flagKey: string) => boolean
): NavShowPermissions => {
  const { capabilities, roles, hasVenues, isWorkspaceInstance, userIsDmn } =
    permissions;

  const basePermissions = roles.derived.hasRoles
    ? getPermissionsFromRoles(
        {
          roles,
          capabilities,
          hasVenues,
          isWorkspaceInstance,
          enviromentsProps
        },
        isFlagEnabled
      )
    : getPermissionsFromCapabilities(
        {
          capabilities,
          hasVenues,
          isWorkspaceInstance,
          userIsDmn,
          enviromentsProps
        },
        isFlagEnabled
      );

  return {
    ...basePermissions,
    showHelpTestAvailability: true
  };
};
